import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  IDLE_PULL,
  MIN_REFRESH_MS,
  canStartPull,
  isArmed,
  reducePull,
  type PullEvent,
  type PullState,
} from '@/components/pwa/pullGesture';

function pageScrollTop(): number {
  return document.scrollingElement?.scrollTop ?? window.scrollY;
}

// Chrome refuses (and logs) vibrate() before the page has seen a tap.
function hapticTick() {
  if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
  navigator.vibrate?.(10);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Native overscroll (iOS bounce chaining, Android's own pull-to-refresh) would fight the custom gesture.
function useContainedOverscroll() {
  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const previous = targets.map((el) => el.style.overscrollBehaviorY);
    targets.forEach((el) => {
      el.style.overscrollBehaviorY = 'contain';
    });
    return () => {
      targets.forEach((el, i) => {
        el.style.overscrollBehaviorY = previous[i] ?? '';
      });
    };
  }, []);
}

export function usePullToRefresh(enabled: boolean): PullState {
  const queryClient = useQueryClient();
  const [view, setView] = useState<PullState>(IDLE_PULL);
  const stateRef = useRef<PullState>(IDLE_PULL);

  useContainedOverscroll();

  useEffect(() => {
    if (!enabled) return;

    const apply = (event: PullEvent): PullState => {
      const prev = stateRef.current;
      const next = reducePull(prev, event);
      if (next === prev) return prev;
      if (!isArmed(prev) && isArmed(next)) hapticTick();
      stateRef.current = next;
      setView(next);
      return next;
    };

    const refresh = async () => {
      await Promise.all([
        queryClient.refetchQueries({ type: 'active' }).catch(() => undefined),
        delay(MIN_REFRESH_MS),
      ]);
      apply({ type: 'refreshed' });
    };

    const onMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch || e.touches.length > 1) {
        detachMove();
        apply({ type: 'cancel' });
        return;
      }
      const prev = stateRef.current;
      const next = apply({ type: 'move', x: touch.clientX, y: touch.clientY });
      const dx = touch.clientX - prev.originX;
      const dy = touch.clientY - prev.originY;
      const downward = dy > 0 && dy >= Math.abs(dx);
      if (e.cancelable && (next.phase === 'pulling' || (next.phase === 'tracking' && downward))) {
        e.preventDefault();
      }
      if (next.phase === 'idle') detachMove();
    };

    let moveAttached = false;
    const attachMove = () => {
      if (moveAttached) return;
      window.addEventListener('touchmove', onMove, { passive: false });
      moveAttached = true;
    };
    function detachMove() {
      if (!moveAttached) return;
      window.removeEventListener('touchmove', onMove);
      moveAttached = false;
    }

    const onStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      if (!touch || e.touches.length !== 1) return;
      if (!canStartPull(e.target, pageScrollTop())) return;
      const next = apply({ type: 'start', x: touch.clientX, y: touch.clientY });
      if (next.phase === 'tracking') attachMove();
    };

    const onEnd = () => {
      detachMove();
      const prev = stateRef.current;
      const next = apply({ type: 'end' });
      if (prev.phase !== 'refreshing' && next.phase === 'refreshing') void refresh();
    };

    const onCancel = () => {
      detachMove();
      apply({ type: 'cancel' });
    };

    window.addEventListener('touchstart', onStart, { passive: true });
    window.addEventListener('touchend', onEnd, { passive: true });
    window.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      window.removeEventListener('touchstart', onStart);
      window.removeEventListener('touchend', onEnd);
      window.removeEventListener('touchcancel', onCancel);
      detachMove();
      apply({ type: 'cancel' });
    };
  }, [enabled, queryClient]);

  return view;
}
